import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";

import { Icon } from "@/components/Icon";
import { Modal } from "@/components/Modal";
import { Toggle } from "@/components/Toggle";
import {
  useCreateUser,
  useResetUserPassword,
  useUpdateUser,
  useUsers,
} from "@/hooks/useUsers";
import { extractApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import type { User } from "@/types/api";

const createSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(120),
  password: z.string().min(8).max(200),
});

const resetSchema = z.object({
  new_password: z.string().min(8).max(200),
});

type CreateValues = z.infer<typeof createSchema>;
type ResetValues = z.infer<typeof resetSchema>;

function initials(name: string) {
  return name
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function UsersPage() {
  const { data, isLoading } = useUsers();
  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<User | null>(null);

  return (
    <div className="view">
      <div className="view__head">
        <div className="view__title-block">
          <div className="view__eyebrow">
            <span className="tick" /> Equipo
          </div>
          <h1 className="view__title">Administradores</h1>
          <p className="view__subtitle">
            Usuarios con acceso al panel del caster. Los cambios quedan firmados en el log de
            auditoría.
          </p>
        </div>
        <div className="view__actions">
          <button className="btn btn--accent" onClick={() => setCreateOpen(true)}>
            <Icon.Plus />
            Invitar admin
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="card card--ticks" style={{ padding: 20 }}>
          <span className="sk" style={{ height: 140 }} />
        </div>
      ) : !data || data.length === 0 ? (
        <div className="card card--ticks">
          <div className="empty">
            <div className="empty__reticle">
              <Icon.Users style={{ width: 18, height: 18 }} />
            </div>
            <div className="empty__title">Aún no hay administradores</div>
            <div className="empty__sub">Invita al primer administrador del caster.</div>
          </div>
        </div>
      ) : (
        <div className="card card--ticks">
          <table className="tbl">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Último acceso</th>
                <th>Creado</th>
                <th style={{ textAlign: "right", paddingRight: 24 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.map((u) => (
                <UserRow key={u.id} user={u} onResetPassword={() => setResetTarget(u)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <ResetPasswordDialog user={resetTarget} onClose={() => setResetTarget(null)} />
    </div>
  );
}

function UserRow({
  user,
  onResetPassword,
}: {
  user: User;
  onResetPassword: () => void;
}) {
  const update = useUpdateUser();
  return (
    <tr>
      <td>
        <div className="row" style={{ gap: 10 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "var(--accent-soft)",
              color: "var(--accent-deep)",
              display: "grid",
              placeItems: "center",
              fontWeight: 600,
              fontSize: 11,
            }}
          >
            {initials(user.name)}
          </div>
          <span style={{ fontWeight: 500 }}>{user.name}</span>
        </div>
      </td>
      <td className="tbl__mono">{user.email}</td>
      <td className="tbl__mono tbl__muted">{formatDateTime(user.last_login_at)}</td>
      <td className="tbl__mono tbl__muted">{formatDateTime(user.created_at)}</td>
      <td style={{ textAlign: "right", paddingRight: 24 }}>
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <span className={`badge ${user.is_enabled ? "badge--ok" : "badge--off"}`}>
            {user.is_enabled ? "Activo" : "Inactivo"}
          </span>
          <Toggle
            checked={user.is_enabled}
            disabled={update.isPending}
            ariaLabel={`Habilitar ${user.email}`}
            onChange={(checked) =>
              update.mutate(
                { id: user.id, is_enabled: checked },
                {
                  onSuccess: () =>
                    toast.success(`${user.email} ${checked ? "habilitado" : "deshabilitado"}`),
                  onError: (e) => toast.error(extractApiError(e)),
                },
              )
            }
          />
          <button className="btn btn--ghost" onClick={onResetPassword}>
            <Icon.Key2 />
            Reset
          </button>
        </div>
      </td>
    </tr>
  );
}

function CreateUserDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreateUser();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { email: "", name: "", password: "" },
  });

  const onSubmit = async (values: CreateValues) => {
    try {
      const u = await create.mutateAsync(values);
      toast.success(`Admin ${u.email} creado`);
      reset();
      onClose();
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nuevo administrador"
      description="El usuario podrá iniciar sesión con este correo y contraseña."
    >
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: "grid", gap: 14 }}>
        <div className="field">
          <label className="field__label">Correo</label>
          <input
            className="input input--mono"
            type="email"
            placeholder="ops@amelia.am"
            {...register("email")}
          />
          {errors.email && <div className="field__error">{errors.email.message}</div>}
        </div>
        <div className="field">
          <label className="field__label">Nombre</label>
          <input className="input" placeholder="Operador" {...register("name")} />
          {errors.name && <div className="field__error">{errors.name.message}</div>}
        </div>
        <div className="field">
          <label className="field__label">Contraseña</label>
          <input className="input input--mono" type="password" {...register("password")} />
          {errors.password && <div className="field__error">{errors.password.message}</div>}
        </div>
        <div className="modal__foot">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancelar
          </button>
          <button type="submit" className="btn btn--accent" disabled={create.isPending}>
            {create.isPending && <span className="spinner" />}
            Crear
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ResetPasswordDialog({
  user,
  onClose,
}: {
  user: User | null;
  onClose: () => void;
}) {
  const reset = useResetUserPassword();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
  } = useForm<ResetValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: { new_password: "" },
  });

  const onSubmit = async (values: ResetValues) => {
    if (!user) return;
    try {
      await reset.mutateAsync({ id: user.id, new_password: values.new_password });
      toast.success(`Contraseña reseteada para ${user.email}`);
      resetForm();
      onClose();
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  return (
    <Modal
      open={!!user}
      onClose={onClose}
      title="Resetear contraseña"
      description={
        user
          ? `Establece una nueva contraseña para ${user.email}. Tendrá que iniciar sesión de nuevo.`
          : ""
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: "grid", gap: 14 }}>
        <div className="field">
          <label className="field__label">Nueva contraseña</label>
          <input className="input input--mono" type="password" {...register("new_password")} />
          {errors.new_password && (
            <div className="field__error">{errors.new_password.message}</div>
          )}
        </div>
        <div className="modal__foot">
          <button type="button" className="btn" onClick={onClose} disabled={reset.isPending}>
            Cancelar
          </button>
          <button type="submit" className="btn btn--accent" disabled={reset.isPending}>
            {reset.isPending && <span className="spinner" />}
            Resetear
          </button>
        </div>
      </form>
    </Modal>
  );
}
